"use client";
import { useEffect, useMemo, useState } from "react";
import { ModalCustom } from "../../../components/custom/modal-custom";
import {
  MEASURE_TYPE,
  MEASURE_TYPE_HINT,
  MEASURE_TYPE_NAME,
  UNIT_STATUS,
  UNIT_STATUS_NAME,
  Unit,
} from "../services/unit-service";

export interface UnitFormValues {
  code: string;
  name: string;
  measure_type: number;
  base_unit_id: number;
  factor: number;
  sort_order: number;
  status?: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: UnitFormValues) => Promise<boolean>;
  loading?: boolean;
  editData?: Unit | null;
  /** Toàn bộ đơn vị, để chọn đơn vị gốc quy đổi. */
  units: Unit[];
}

const UnitModal: React.FC<Props> = ({ isOpen, onClose, onSubmit, loading, editData, units }) => {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [measureType, setMeasureType] = useState<number>(MEASURE_TYPE.COUNT);
  const [baseUnitId, setBaseUnitId] = useState<number>(0);
  const [factor, setFactor] = useState("1");
  const [sortOrder, setSortOrder] = useState("0");
  const [status, setStatus] = useState<number>(UNIT_STATUS.ACTIVE);

  useEffect(() => {
    if (editData) {
      setCode(editData.code || "");
      setName(editData.name || "");
      setMeasureType(editData.measure_type ?? MEASURE_TYPE.COUNT);
      setBaseUnitId(editData.base_unit_id ?? 0);
      setFactor(String(editData.factor ?? 1));
      setSortOrder(String(editData.sort_order ?? 0));
      setStatus(editData.status);
    } else {
      setCode("");
      setName("");
      setMeasureType(MEASURE_TYPE.COUNT);
      setBaseUnitId(0);
      setFactor("1");
      setSortOrder("0");
      setStatus(UNIT_STATUS.ACTIVE);
    }
  }, [editData, isOpen]);

  /**
   * Chỉ cho chọn đơn vị gốc CÙNG nhóm đo lường — quy đổi ml sang cái là vô nghĩa, và backend cũng
   * từ chối. Loại luôn chính nó để không tự quy đổi về mình.
   */
  const baseCandidates = useMemo(
    () =>
      units.filter(
        (u) => u.measure_type === measureType && u.id !== editData?.id && !u.base_unit_id
      ),
    [units, measureType, editData]
  );

  // Đổi nhóm đo lường thì đơn vị gốc cũ không còn hợp lệ.
  useEffect(() => {
    if (baseUnitId && !baseCandidates.some((u) => u.id === baseUnitId)) {
      setBaseUnitId(0);
      setFactor("1");
    }
  }, [baseCandidates, baseUnitId]);

  const selectedBase = units.find((u) => u.id === baseUnitId);
  const canSubmit = code.trim().length > 0 && name.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const values: UnitFormValues = {
      code: code.trim().toUpperCase(),
      name: name.trim(),
      measure_type: measureType,
      base_unit_id: baseUnitId || 0,
      factor: baseUnitId ? Number(factor) || 0 : 1,
      sort_order: Number(sortOrder) || 0,
    };
    if (editData) values.status = status;

    const ok = await onSubmit(values);
    if (ok) onClose();
  };

  const bodyModal = (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">
            Mã đơn vị <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="VD: KG, CAI, ML"
            className="w-full border rounded px-3 py-2 text-sm uppercase"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            Tên hiển thị <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="VD: kg, cái, ml"
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">
          Nhóm đo lường <span className="text-red-500">*</span>
        </label>
        <select
          value={measureType}
          onChange={(e) => setMeasureType(Number(e.target.value))}
          className="w-full border rounded px-3 py-2 text-sm"
        >
          {Object.entries(MEASURE_TYPE_NAME).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-400 mt-1">{MEASURE_TYPE_HINT[measureType]}</p>
        {/* Nhóm đo lường quyết định vật tư có vào kiểm đếm hay không — nói rõ hệ quả. */}
        {measureType === MEASURE_TYPE.COUNT && (
          <p className="text-xs text-blue-600 mt-1">
            Vật tư mang đơn vị nhóm này sẽ vào biên bản kiểm đếm và sổ nợ đồ.
          </p>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 p-3 space-y-3">
        <div className="text-sm font-medium text-gray-700">Quy đổi (cho phần định lượng)</div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Quy về đơn vị gốc</label>
            <select
              value={baseUnitId}
              onChange={(e) => setBaseUnitId(Number(e.target.value))}
              className="w-full border rounded px-3 py-2 text-sm"
            >
              <option value={0}>— Đây là đơn vị gốc —</option>
              {baseCandidates.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Hệ số</label>
            <input
              type="number"
              step="0.000001"
              min="0"
              value={baseUnitId ? factor : ""}
              disabled={!baseUnitId}
              onChange={(e) => setFactor(e.target.value)}
              placeholder="VD: 0.001"
              className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100"
            />
          </div>
        </div>

        {baseUnitId && selectedBase ? (
          <div className="rounded bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
            Nghĩa là: <b>1 {name || "đơn vị này"} = {factor || 0} {selectedBase.name}</b>
          </div>
        ) : (
          <p className="text-xs text-gray-400">
            Chỉ chọn đơn vị gốc cùng nhóm đo lường. Thiếu hệ số thì phần định lượng không cộng trừ
            được tiêu hao.
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Thứ tự hiển thị</label>
          <input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>
        {editData && (
          <div>
            <label className="block text-sm font-medium mb-1">Trạng thái</label>
            <select
              value={status}
              onChange={(e) => setStatus(Number(e.target.value))}
              className="w-full border rounded px-3 py-2 text-sm"
            >
              {Object.entries(UNIT_STATUS_NAME).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );

  const footerModal = (
    <div className="flex gap-2">
      <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded hover:bg-gray-50">
        Hủy
      </button>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={loading || !canSubmit}
        className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
      >
        {loading ? "Đang xử lý..." : editData ? "Cập nhật" : "Tạo mới"}
      </button>
    </div>
  );

  return (
    <ModalCustom
      titleModal={editData ? "Cập nhật đơn vị" : "Thêm đơn vị"}
      isOpen={isOpen}
      onClose={() => onClose()}
      bodyModal={bodyModal}
      footerModal={footerModal}
      modalSize="lg"
    />
  );
};

export default UnitModal;
